import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PawPrint } from 'lucide-react';
import { fetchAllPets, PetWithOwner } from '../utils/api';
import { petSpeciesIcon, KNOWN_SPECIES } from '../utils/petUtils';

type FilterSpecies = 'all' | string;

const speciesLabel = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const PetCard = ({ pet }: { pet: PetWithOwner }) => {
  const SpeciesIcon = petSpeciesIcon(pet.species);
  return (
    <div className="group flex flex-col rounded-2xl border border-bolt-border bg-bolt-surface overflow-hidden hover:border-emerald-500/40 hover:shadow-lg hover:shadow-emerald-500/5 transition-all duration-200">
      <div className="relative">
        {pet.photoUrl ? (
          <img
            src={pet.photoUrl}
            alt={pet.name}
            className="w-full h-44 object-cover"
          />
        ) : (
          <div className="w-full h-44 bg-emerald-500/10 flex items-center justify-center">
            <SpeciesIcon className="h-16 w-16 text-emerald-400/60" />
          </div>
        )}
        <div className="absolute top-2.5 right-2.5">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-bolt-bg/80 backdrop-blur-sm text-bolt-textSubtle border border-bolt-border/60">
            <SpeciesIcon className="h-2.5 w-2.5" /> {speciesLabel(pet.species)}
          </span>
        </div>
      </div>

      <div className="flex flex-col flex-1 p-4 gap-1.5">
        <div>
          <h3 className="text-sm font-semibold text-bolt-text leading-tight">{pet.name}</h3>
          {pet.role && (
            <p className="text-xs text-emerald-400 font-medium mt-0.5">{pet.role}</p>
          )}
        </div>
        {pet.bio && (
          <p className="text-xs text-bolt-textMuted leading-relaxed line-clamp-3">{pet.bio}</p>
        )}

        <div className="mt-auto pt-3 border-t border-bolt-border/60">
          <Link
            to={`/employee/${pet.ownerSlug}`}
            className="flex items-center gap-2 group/owner"
          >
            {pet.ownerAvatar ? (
              <img
                src={pet.ownerAvatar}
                alt={pet.ownerName}
                className="w-5 h-5 rounded-full object-cover flex-shrink-0 border border-bolt-border"
              />
            ) : (
              <div className="w-5 h-5 rounded-full bg-bolt-border flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-semibold text-bolt-textSubtle">
                  {pet.ownerName.charAt(0)}
                </span>
              </div>
            )}
            <span className="text-xs text-bolt-textMuted group-hover/owner:text-emerald-400 transition-colors truncate">
              {pet.ownerName}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

const SpeciesSkeleton = () => (
  <div className="mb-12">
    <div className="h-6 w-32 bg-bolt-border/50 rounded-md mb-5 animate-pulse" />
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-bolt-border bg-bolt-surface overflow-hidden animate-pulse">
          <div className="w-full h-44 bg-bolt-border/30" />
          <div className="p-4 space-y-2">
            <div className="h-3 w-24 bg-bolt-border/50 rounded" />
            <div className="h-3 w-16 bg-bolt-border/30 rounded" />
            <div className="h-3 w-full bg-bolt-border/20 rounded" />
            <div className="h-3 w-3/4 bg-bolt-border/20 rounded" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const PetsPage = () => {
  const [pets, setPets] = useState<PetWithOwner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterSpecies>('all');

  useEffect(() => {
    fetchAllPets()
      .then(setPets)
      .finally(() => setIsLoading(false));
  }, []);

  const speciesOrder = useMemo(() => {
    const presentSpecies = new Set(pets.map(p => p.species.toLowerCase()));
    const ordered = KNOWN_SPECIES.filter(s => presentSpecies.has(s));
    const unknowns = [...presentSpecies].filter(s => !KNOWN_SPECIES.includes(s as (typeof KNOWN_SPECIES)[number]));
    return [...ordered, ...unknowns];
  }, [pets]);

  const grouped = useMemo(() => {
    const map = new Map<string, PetWithOwner[]>();
    for (const pet of pets) {
      const key = pet.species.toLowerCase();
      const arr = map.get(key) ?? [];
      arr.push(pet);
      map.set(key, arr);
    }
    return map;
  }, [pets]);

  const visibleSpecies = activeFilter === 'all' ? speciesOrder : [activeFilter];

  return (
    <div className="container-custom py-10">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center">
            <PawPrint className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-bolt-text leading-tight">Company Pets</h1>
            {!isLoading && (
              <p className="text-sm text-bolt-textSubtle">
                {pets.length} {pets.length === 1 ? 'pet' : 'pets'} across the team
              </p>
            )}
          </div>
        </div>
        <p className="text-sm text-bolt-textMuted max-w-xl">
          Meet the furry, feathered, and scaly members of the StackBlitz team.
        </p>
      </div>

      {/* Filter pills */}
      {!isLoading && speciesOrder.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveFilter('all')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
              activeFilter === 'all'
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
                : 'bg-bolt-surface border-bolt-border text-bolt-textMuted hover:text-bolt-text hover:border-bolt-border/80'
            }`}
          >
            All
            <span className={`rounded-full px-1.5 py-px text-[10px] ${activeFilter === 'all' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-bolt-border/50 text-bolt-textSubtle'}`}>
              {pets.length}
            </span>
          </button>
          {speciesOrder.map(species => {
            const count = grouped.get(species)?.length ?? 0;
            const active = activeFilter === species;
            const SIcon = petSpeciesIcon(species);
            return (
              <button
                key={species}
                onClick={() => setActiveFilter(species)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                  active
                    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
                    : 'bg-bolt-surface border-bolt-border text-bolt-textMuted hover:text-bolt-text hover:border-bolt-border/80'
                }`}
              >
                <SIcon className="h-3 w-3" /> {speciesLabel(species)}
                <span className={`rounded-full px-1.5 py-px text-[10px] ${active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-bolt-border/50 text-bolt-textSubtle'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <>
          <SpeciesSkeleton />
          <SpeciesSkeleton />
        </>
      ) : pets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
            <PawPrint className="h-8 w-8 text-emerald-500/50" />
          </div>
          <h2 className="text-base font-semibold text-bolt-text mb-1">No pets yet</h2>
          <p className="text-sm text-bolt-textMuted max-w-sm">
            Team members can add their pets from their profile page.
          </p>
        </div>
      ) : (
        <div className="space-y-12">
          {visibleSpecies.map(species => {
            const speciesPets = grouped.get(species) ?? [];
            const SectionIcon = petSpeciesIcon(species);
            return (
              <section key={species}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <SectionIcon className="h-4 w-4 text-emerald-400" />
                  </div>
                  <h2 className="text-lg font-bold text-bolt-text">{speciesLabel(species)}s</h2>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    {speciesPets.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {speciesPets.map(pet => (
                    <PetCard key={pet.id} pet={pet} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PetsPage;
